import {
  Building,
  ChangeToSiteDefaults,
  getESysName,
  RecursivePartialSiteAllowStringValues,
  SiteAllowStringValues,
  SiteElectricalSystem,
  SiteElectricalSystemPlaceholder,
  SiteFile,
  SiteOwnership,
} from "@inrange/building-manager-api-client/models-site";
import {
  SiteCalculationsContext,
  useContextTS,
} from "@inrange/shared-components";
import { Modal, ModalView } from "@inrange/theme-components";
import { Icons } from "@inrange/theme-components/icons";
import React, { ReactNode, useEffect, useRef, useState } from "react";
import { Alert } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import { CollapsibleCardColorProvider } from "../../utils/CollapsibleCardColorContext";
import { getCardColor } from "../../utils/collapsible-card-colors";
import useIsMobile from "../../utils/useIsMobile";
import CollapsibleCard from "./CollapsibleCard";
import ConfirmChangesModal from "./ConfirmChangesModal";
import SaveWarningsModal from "./SaveWarningsModal";
import SiteScoreSuggestionModal from "./SiteScoreSuggestionModal";
import { useSetDefaultsOnCountryChange } from "./siteEditHooks";
import ActionButtons from "./siteSections/ActionButtons";
import ActivityLog from "./siteSections/ActivityLog";
import Battery from "./siteSections/Battery";
import BatterySummary from "./siteSections/BatterySummary";
import Capex from "./siteSections/Capex";
import ChartForecast from "./siteSections/ChartForecast";
import Cost from "./siteSections/Cost";
import DealTerms from "./siteSections/DealTerms";
import Demand from "./siteSections/Demand";
import DnoNetwork from "./siteSections/DnoNetwork";
import ExportNetworkTariff from "./siteSections/ExportNetworkTariff";
import FinancialPerformanceCustomer from "./siteSections/FinancialPerformanceCustomer";
import FinancialPerformanceInRange from "./siteSections/FinancialPerformanceInRange";
import Generation from "./siteSections/Generation";
import LockNote from "./siteSections/LockNote";
import Notes from "./siteSections/Notes";
import PvSystemComponent from "./siteSections/PVSystem/PVSystem";
import SiteFeatureFlags from "./siteSections/SiteFeatureFlags";
import SiteGeo from "./siteSections/SiteGeo/SiteGeo";
import SiteInformation from "./siteSections/SiteInformation/SiteInformation";
import SiteNav from "./siteSections/SiteNav";
import SupplierTariff from "./siteSections/SupplierTariff";

type BuildingWithKey = Building & {
  key: string;
  surface_area_sqm: number;
  customised?: boolean;
};

type OwnershipInput = {
  ownershipIndex: number;
  selectedID: string;
  selectedName: string;
  selectedType: string;
  selectedPark?: string;
};

type NavigationBlocker = {
  state: "blocked" | "unblocked" | "proceeding";
  reset: () => void;
  proceed: () => void;
};

export type SiteEditViewProps = {
  // Common props
  site: SiteAllowStringValues;
  setSite: (site: RecursivePartialSiteAllowStringValues) => void;
  pendingUpdateSite?: ChangeToSiteDefaults[];
  setPendingUpdateSite: (update: ChangeToSiteDefaults[] | undefined) => void;
  hasUnsavedBuildings: boolean;
  setHasUnsavedBuildings: (value: boolean) => void;
  onSaveClick: (withExtraUpdates?: {
    site?: RecursivePartialSiteAllowStringValues;
    [key: string]: any;
  }) => void;
  saveSite: (withExtraUpdates?: {
    site?: RecursivePartialSiteAllowStringValues;
    [key: string]: any;
  }) => void;
  setBuildingsForPreview: (buildings: BuildingWithKey[]) => void;
  ownerships: SiteOwnership[];
  onOwnerAdded: (owner: OwnershipInput) => void;
  isOwnerOccupied: boolean;
  saveDisabled: boolean;
  setSaveDisabled: (value: boolean) => void;
  saveWarnings?: string[];
  setSaveWarnings: (warnings: string[] | undefined) => void;
  siteStatusMessage?: ReactNode | string | undefined;
  alertStyle: string;
  setAlertStyle: (style: string) => void;
  alertMessage: ReactNode | string | undefined;
  setAlertMessage: (
    message: ReactNode | string | undefined | undefined
  ) => void;

  // Mode
  mode?: "new" | "edit";

  // New site only props
  siteNewURLBuildingID?: string;
  setNewSiteName?: (
    siteName: string,
    updatedOwnerships: SiteOwnership[]
  ) => void;

  // Edit site only props
  siteUpdates?: RecursivePartialSiteAllowStringValues;
  siteBuildings?: Building[];
  selectPvDesign?: (installedCapacity: number) => void;
  pvDesignInstalledCapacity?: number;
  setPvDesignInstalledCapacity?: (installedCapacity: number) => void;
  deselectPvDesign?: () => void;
  onOwnerRemoved?: (ownership: SiteOwnership) => void;
  onOwnerEdited?: React.Dispatch<
    React.SetStateAction<Record<string, SiteOwnership>>
  >;
  siteFiles?: SiteFile[];
  onFileAdded?: (file: SiteFile) => void;
  onFileDeleted?: (file: SiteFile) => void;
  onDeleteSiteClick?: () => void;
  pvDesignUploadRef?: React.RefObject<HTMLInputElement | null>;
  userEmail?: string;
  handlePreviewChangelog?: () => void;
  withExtraUpdatesFromModal?: {
    site?: RecursivePartialSiteAllowStringValues;
    [key: string]: any;
  };
  navigationBlocker?: NavigationBlocker;
};

const SiteEditView = ({
  // Common props
  site,
  setSite,
  pendingUpdateSite,
  setPendingUpdateSite,
  hasUnsavedBuildings,
  setHasUnsavedBuildings,
  onSaveClick,
  saveSite,
  setBuildingsForPreview,
  ownerships,
  onOwnerAdded,
  isOwnerOccupied,
  saveDisabled,
  setSaveDisabled,
  saveWarnings,
  setSaveWarnings,
  siteStatusMessage,
  alertStyle,
  setAlertStyle,
  alertMessage,
  setAlertMessage,
  // Mode
  mode,
  // New site only props
  siteNewURLBuildingID,
  setNewSiteName,
  // Edit site only props
  siteUpdates,
  siteBuildings,
  selectPvDesign,
  pvDesignInstalledCapacity,
  setPvDesignInstalledCapacity,
  deselectPvDesign,
  onOwnerRemoved,
  onOwnerEdited,
  siteFiles,
  onFileAdded,
  onFileDeleted,
  onDeleteSiteClick,
  pvDesignUploadRef,
  userEmail,
  withExtraUpdatesFromModal,
  handlePreviewChangelog,
  navigationBlocker,
}: SiteEditViewProps) => {
  const { siteCalculations } = useContextTS(SiteCalculationsContext);
  const isMobile = useIsMobile();

  // Use a ref to the installed capacity so that the following useEffect is only triggered when
  // kwpPerSqm or totalBuildingArea changes, but that the user can still manually edit installed
  // capacity if they want to.
  //
  // We use a UseEffect rather than hooking into the setSiteBuildings click handler as this might be
  // clicked before kwpPerSqm has been loaded from the backend.
  const installedCapacityRef = useRef<string | number | undefined>(undefined);
  installedCapacityRef.current = site.generation?.[0]?.installedCapacity ?? 0;
  const kwpPerSqm =
    siteCalculations.generation?.[0]?.halfHourlyGeneration?.kwpPerSqm ?? 0;
  useEffect(() => {
    if (mode === "edit") {
      return;
    }
    const totalArea = site.buildings
      .map((b) => b.surfaceAreaSqM)
      .map((value) => parseFloat(value as string))
      .reduce((sum, value) => sum + value, 0);
    const installedCapacity = totalArea * 0.35 * kwpPerSqm;
    if (
      installedCapacityRef.current !== installedCapacity &&
      installedCapacity > 0
    ) {
      setSite({
        generation: {
          [0]: {
            installedCapacity: installedCapacity,
          },
        },
      });
    }
  }, [setSite, kwpPerSqm, site.buildings, mode]);

  useSetDefaultsOnCountryChange(
    site,
    setSite,
    siteCalculations.countryCode,
    siteCalculations.currencyCode
  );

  const handleAddSiteMatch = () => {
    const esysIndices = Object.keys(site.esys)
      .filter((key) => !!site.esys[key])
      .map(Number);
    const maxIndex = esysIndices.length > 0 ? Math.max(...esysIndices) : -1;
    const newIndex = maxIndex + 1;

    const newEsys = {
      idx: newIndex,
      genIdx: newIndex,
      demandIdx: newIndex,
      batteryIdx: newIndex,
    };

    setSite({
      esys: {
        ...site.esys,
        [newIndex]: newEsys,
      },
      demand: {
        [newIndex]: {
          idx: newIndex,
          buildingProfile: site.demand[maxIndex]?.buildingProfile || undefined,
          demandCoefficientKWhPerM2:
            site.demand[maxIndex]?.demandCoefficientKWhPerM2 || undefined,
          areaPct: 0,
        },
      },
      generation: {
        [newIndex]: {
          idx: newIndex,
          installedCapacity: 0,
        },
      },
      battery: {
        [newIndex]: {
          idx: newIndex,
        },
      },
    });
  };

  const handleDeleteLastSiteMatch = () => {
    const esysIndices = Object.keys(site.esys)
      .filter((key) => !!site.esys[key])
      .map(Number)
      .sort((a, b) => a - b);
    if (esysIndices.length <= 1) {
      return; // Don't allow removing the first match
    }

    const lastIndex = esysIndices[esysIndices.length - 1];

    setSite({
      esys: {
        ...site.esys,
        [lastIndex]: null as unknown as SiteElectricalSystem,
      },
      demand: {
        ...site.demand,
        [lastIndex]: null as unknown as SiteElectricalSystem,
      },
      generation: {
        ...site.generation,
        [lastIndex]: null as unknown as SiteElectricalSystem,
      },
      battery: {
        ...site.battery,
        [lastIndex]: null as unknown as SiteElectricalSystem,
      },
    });
  };

  const summaryEsys: SiteElectricalSystemPlaceholder = {
    idx: "summary",
    genIdx: "summary",
    demandIdx: "summary",
    batteryIdx: "summary",
  };

  const esysCount = Object.values(site.esys).filter((m) => !!m).length;

  const [pendingScoreSuggestion, setPendingScoreSuggestion] = useState<
    | {
        withExtraUpdates?: {
          site?: RecursivePartialSiteAllowStringValues;
          [key: string]: any;
        };
      }
    | undefined
  >(undefined);

  const currentScore =
    typeof site.score === "string"
      ? site.score === ""
        ? null
        : Number(site.score)
      : (site.score ?? null);
  const suggestedScore = siteCalculations.suggestedScore ?? null;
  const suggestedScoreFactors = siteCalculations.suggestedScoreFactors ?? [];
  const testMode = !import.meta.env.PROD && import.meta.env.VITE_TEST_MODE;
  const handleSaveClickWithScoreCheck = (withExtraUpdates?: {
    site?: RecursivePartialSiteAllowStringValues;
    [key: string]: any;
  }) => {
    const overrideScore = withExtraUpdates?.site?.score;
    const effectiveScore =
      overrideScore !== undefined ? overrideScore : currentScore;
    if (
      !testMode &&
      suggestedScore !== null &&
      suggestedScore !== effectiveScore
    ) {
      setPendingScoreSuggestion({ withExtraUpdates });
      return;
    }
    onSaveClick(withExtraUpdates);
  };

  return (
    <>
      {mode === "edit" && <SiteNav />}
      <Container fluid style={{ padding: "12px 12px 0px 12px" }}>
        {siteStatusMessage && (
          <Alert variant="secondary" dismissible={false}>
            <div>{siteStatusMessage}</div>
          </Alert>
        )}
        {site.locked && (
          <LockNote lockNote={site.lockNote || ""} setSite={setSite} />
        )}
        {alertMessage && (
          <Alert
            variant={alertStyle}
            onClose={() => setAlertMessage(undefined)}
            dismissible={alertStyle !== "secondary"}
          >
            <div>{alertMessage}</div>
          </Alert>
        )}
        <Row>
          <Col md>
            <SiteGeo
              site={site}
              initialSiteBuildings={mode === "edit" ? siteBuildings : undefined}
              setSite={setSite}
              setBuildingsForPreview={setBuildingsForPreview}
              siteNewURLBuildingID={siteNewURLBuildingID}
              ownerships={mode === "edit" ? undefined : ownerships}
              setNewSiteName={mode === "edit" ? () => {} : setNewSiteName} // We don't want to change the site name of a created site
              setHasUnsavedBuildings={setHasUnsavedBuildings}
            />
            {isMobile && (
              <div className="mt-2">
                <SiteInformation
                  site={site}
                  offtakerOnlySite={
                    (siteCalculations.energyFlowAnnual?.generation ?? 0) === 0
                  }
                  setSite={setSite}
                  ownerships={ownerships}
                  onOwnerAdded={onOwnerAdded}
                  onOwnerRemoved={mode === "edit" ? onOwnerRemoved : () => {}}
                  onOwnerEdited={mode === "edit" ? onOwnerEdited : undefined}
                  onSaveClick={onSaveClick}
                  setNewSiteName={
                    mode === "edit" || !setNewSiteName
                      ? () => {}
                      : setNewSiteName
                  } // We don't want to change the site name of a created site
                  allowCopy={mode === "edit"}
                  siteUpdates={mode === "edit" ? siteUpdates : undefined}
                  setAlertStyle={setAlertStyle}
                  setAlertMessage={setAlertMessage}
                />
              </div>
            )}
            {esysCount > 1 && (
              <fieldset disabled={site.locked}>
                <CollapsibleCard
                  title={`Site energy flow`}
                  className="mt-2"
                  isWrapper={true}
                  menu={
                    <Button
                      onClick={handleAddSiteMatch}
                      variant="success"
                      style={{ padding: "2px 10px" }}
                      data-testid="addSiteMatchButton"
                    >
                      +
                    </Button>
                  }
                >
                  <Demand
                    site={site}
                    esys={summaryEsys}
                    setSite={setSite}
                    editMode="SUMMARY"
                  />
                  <Generation
                    site={site}
                    esys={summaryEsys}
                    setSite={setSite}
                    setPendingUpdateSite={setPendingUpdateSite}
                    siteFiles={siteFiles}
                    onFileAdded={onFileAdded}
                    onFileDeleted={onFileDeleted}
                    setSaveDisabled={setSaveDisabled}
                    pvDesignUploadRef={pvDesignUploadRef}
                    pvDesignInstalledCapacity={pvDesignInstalledCapacity}
                    editMode="SUMMARY"
                  />
                  {siteCalculations.totalBatterySummary && (
                    <BatterySummary site={site} setSite={setSite} />
                  )}
                </CollapsibleCard>
              </fieldset>
            )}
            {Object.values(site.esys)
              .filter((e) => !!e)
              .map((esys, index) => {
                const editMode = esysCount > 1 ? "ONE_OF_MULTIPLE" : "SINGLE";
                const matchSuffix = getESysName(esysCount, esys);
                const siteMatch = (
                  <React.Fragment key={esys.idx}>
                    <fieldset disabled={site.locked}>
                      <Demand
                        site={site}
                        esys={esys}
                        matchSuffix={matchSuffix}
                        setSite={setSite}
                        editMode={editMode}
                        handleAddSiteMatch={
                          editMode === "SINGLE" ? handleAddSiteMatch : undefined
                        }
                      />
                      <Generation
                        site={site}
                        esys={esys}
                        matchSuffix={matchSuffix}
                        setSite={setSite}
                        setPendingUpdateSite={setPendingUpdateSite}
                        siteFiles={siteFiles}
                        onFileAdded={onFileAdded}
                        onFileDeleted={onFileDeleted}
                        setSaveDisabled={setSaveDisabled}
                        pvDesignUploadRef={pvDesignUploadRef}
                        pvDesignInstalledCapacity={
                          editMode === "SINGLE"
                            ? pvDesignInstalledCapacity
                            : undefined
                        }
                        editMode={editMode}
                      />
                      {Number(
                        site.generation[esys.genIdx]?.installedCapacity ?? 0
                      ) > 0 && (
                        <Battery
                          site={site}
                          esys={esys}
                          matchSuffix={matchSuffix}
                          setSite={setSite}
                          currencyCode={siteCalculations.currencyCode}
                          isOwnerOccupied={isOwnerOccupied}
                          editMode={editMode}
                        />
                      )}
                    </fieldset>
                  </React.Fragment>
                );
                if (esysCount > 1) {
                  return (
                    <CollapsibleCardColorProvider
                      color={getCardColor(parseInt(esys.idx as string))}
                      key={esys.idx}
                    >
                      <CollapsibleCard
                        title={`Energy match ${matchSuffix}`}
                        className="mt-2"
                        isWrapper={true}
                        menu={
                          index === esysCount - 1 ? (
                            <div
                              onClick={handleDeleteLastSiteMatch}
                              data-testid="deleteLastSiteMatchButton"
                            >
                              <img
                                alt="remove"
                                src={Icons.trashcanRed}
                                style={{
                                  width: "20px",
                                  height: "20px",
                                  marginLeft: "6px",
                                  cursor: "pointer",
                                }}
                              ></img>
                            </div>
                          ) : undefined
                        }
                      >
                        {siteMatch}
                      </CollapsibleCard>
                    </CollapsibleCardColorProvider>
                  );
                }
                return siteMatch;
              })}

            {mode === "edit" &&
              selectPvDesign &&
              deselectPvDesign &&
              setPvDesignInstalledCapacity &&
              userEmail && (
                <PvSystemComponent
                  site={site}
                  selectPvDesign={selectPvDesign}
                  deselectPvDesign={deselectPvDesign}
                  setPvDesignInstalledCapacity={setPvDesignInstalledCapacity}
                  userEmail={userEmail}
                />
              )}
            <Notes
              notes={site.notes}
              siteLocked={site.locked}
              setSite={setSite}
            />
            <fieldset disabled={site.locked}>
              <ChartForecast site={site} />
              <SiteFeatureFlags
                site={site}
                setSite={setSite}
                isBulkEdit={false}
              />
            </fieldset>
          </Col>
          <Col md>
            {!isMobile && (
              <SiteInformation
                site={site}
                offtakerOnlySite={
                  (siteCalculations.energyFlowAnnual?.generation ?? 0) === 0
                }
                setSite={setSite}
                ownerships={ownerships}
                onOwnerAdded={onOwnerAdded}
                onOwnerRemoved={mode === "edit" ? onOwnerRemoved : () => {}}
                onOwnerEdited={mode === "edit" ? onOwnerEdited : undefined}
                onSaveClick={onSaveClick}
                setNewSiteName={
                  mode === "edit" || !setNewSiteName ? () => {} : setNewSiteName
                } // We don't want to change the site name of a created site
                allowCopy={mode === "edit"}
                siteUpdates={mode === "edit" ? siteUpdates : undefined}
                setAlertStyle={setAlertStyle}
                setAlertMessage={setAlertMessage}
              />
            )}
            <fieldset disabled={site.locked}>
              <DealTerms
                site={site}
                setSite={setSite}
                setPendingUpdateSite={setPendingUpdateSite}
                isOwnerOccupied={isOwnerOccupied}
              />
              <ExportNetworkTariff site={site} setSite={setSite} />
              <SupplierTariff site={site} setSite={setSite} />
              <Cost site={site} setSite={setSite} />
              <Capex site={site} currencyCode={siteCalculations.currencyCode} />
              <FinancialPerformanceCustomer
                site={site}
                isOwnerOccupied={isOwnerOccupied}
              />
              <FinancialPerformanceInRange
                site={site}
                setSite={setSite}
                isOwnerOccupied={isOwnerOccupied}
              />
              <DnoNetwork site={site} setSite={setSite} />
            </fieldset>
            {mode === "edit" && (
              <ActivityLog
                siteId={site.id}
                createdAt={site.createdAt as unknown as string}
                createdBy={site.createdBy}
              />
            )}
          </Col>
        </Row>
        <Row>
          <ActionButtons
            site={site}
            handleDeleteClick={onDeleteSiteClick}
            handlePreviewChangelog={handlePreviewChangelog}
            handleSaveClick={
              mode === "edit"
                ? (withExtraUpdates) =>
                    handleSaveClickWithScoreCheck(withExtraUpdates)
                : (_) => handleSaveClickWithScoreCheck()
            }
            saveDisabled={saveDisabled || hasUnsavedBuildings}
            allowNote={mode === "edit"}
          />
        </Row>
        {saveWarnings && (
          <SaveWarningsModal
            saveWarnings={saveWarnings}
            setSaveWarnings={setSaveWarnings}
            withExtraUpdatesFromModal={withExtraUpdatesFromModal}
            saveSite={saveSite}
            setSaveDisabled={setSaveDisabled}
          />
        )}
        {pendingUpdateSite && (
          <ConfirmChangesModal
            site={{ ...site, ...siteCalculations }}
            setSite={setSite}
            pendingUpdateSite={pendingUpdateSite}
            setPendingUpdateSite={setPendingUpdateSite}
          />
        )}
        {pendingScoreSuggestion && suggestedScore !== null && (
          <SiteScoreSuggestionModal
            currentScore={currentScore}
            suggestedScore={suggestedScore}
            factors={suggestedScoreFactors}
            onKeepCurrent={() => {
              const extras = pendingScoreSuggestion.withExtraUpdates;
              setPendingScoreSuggestion(undefined);
              onSaveClick(extras);
            }}
            onAcceptSuggested={() => {
              const extras = pendingScoreSuggestion.withExtraUpdates;
              setPendingScoreSuggestion(undefined);
              onSaveClick({
                ...extras,
                site: {
                  ...(extras?.site ?? {}),
                  score: suggestedScore,
                  scoreFactors: suggestedScoreFactors,
                },
              });
            }}
          />
        )}
        {navigationBlocker && navigationBlocker.state === "blocked" && (
          <Modal>
            <ModalView
              title="You have unsaved changes. Do you want to continue?"
              fontWeight="500"
              fontSize="16px"
              width="600px"
              titlePadding="0"
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "30px",
                  marginTop: "30px",
                }}
              >
                <Button
                  variant="secondary"
                  onClick={() => navigationBlocker.reset()}
                  style={{ flex: "1" }}
                >
                  No, go back
                </Button>
                <Button
                  variant="danger"
                  onClick={() => navigationBlocker.proceed()}
                  style={{ flex: "1" }}
                >
                  Yes, discard changes
                </Button>
              </div>
            </ModalView>
          </Modal>
        )}
      </Container>
    </>
  );
};

export default SiteEditView;
